import { useState } from "react";
import { Phone, Mail, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Field } from "../components/Field";
import { PageHero } from "../components/SectionHeading";
import { useBrand } from "../context/BrandContext";
import { api, errorMessage } from "../lib/api";

const EMPTY = { name: "", email: "", phone: "", message: "" };
const validate = (f) => {
  const e = {};
  if (f.name.trim().length < 2) e.name = "Please enter your name.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = "Enter a valid email address.";
  if (f.message.trim().length < 5) e.message = "Please write a short message.";
  return e;
};

const ContactForm = () => {
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(f);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      await api.post("/contact", { ...f, phone: f.phone || null });
      setSent(true);
      setF(EMPTY);
      toast.success("Message received. We'll get back to you shortly.");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="panel p-10 text-center" data-testid="contact-success">
        <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
        <div className="mt-5 font-display font-bold uppercase text-slate-900 text-3xl">Message received</div>
        <p className="mt-3 text-slate-600">Thank you. A coordinator will reply by email or phone.</p>
        <button onClick={() => setSent(false)} className="btn-ghost mt-8" data-testid="contact-send-another">Send another message</button>
      </div>
    );
  }
  return (
    <form onSubmit={submit} noValidate className="panel p-6 sm:p-8 space-y-5" data-testid="contact-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field id="name" label="Name *" error={errors.name}><Input id="name" value={f.name} onChange={set("name")} className="field-input" data-testid="contact-input-name" /></Field>
        <Field id="email" label="Email *" error={errors.email}><Input id="email" type="email" value={f.email} onChange={set("email")} className="field-input" data-testid="contact-input-email" /></Field>
      </div>
      <Field id="phone" label="Phone"><Input id="phone" type="tel" value={f.phone} onChange={set("phone")} className="field-input" data-testid="contact-input-phone" /></Field>
      <Field id="message" label="Message *" error={errors.message}>
        <Textarea id="message" rows={6} value={f.message} onChange={set("message")} className="rounded-none bg-white border-slate-300 focus-visible:ring-amber-500" data-testid="contact-input-message" />
      </Field>
      <button type="submit" disabled={loading} className="btn-amber w-full sm:w-auto" data-testid="contact-submit-button">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send message"}
      </button>
    </form>
  );
};

export default function Contact() {
  const b = useBrand();
  const items = [
    { icon: MapPin, label: "Address", value: b.address_full, id: "address" },
    { icon: Phone, label: "Phone", value: b.phone_display, href: `tel:${b.phone_tel}`, id: "phone" },
    { icon: Mail, label: "Email", value: b.email, href: `mailto:${b.email}`, id: "email" },
  ];
  return (
    <>
      <PageHero testid="contact-hero" eyebrow="Contact" title="Talk to our team" sub="Questions about a route, a vehicle or an invoice? Call, email or send us a message." />
      <section className="py-20 md:py-24">
        <div className="container-x grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7"><ContactForm /></div>
          <div className="lg:col-span-5 space-y-4">
            {items.map(({ icon: I, label, value, href, id }) => {
              const Tag = href ? "a" : "div";
              return (
                <Tag key={id} href={href} className="panel p-5 flex gap-4 items-start lift" data-testid={`contact-info-${id}`}>
                  <I className="w-5 h-5 text-orange-600 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
                    <div className="mt-1 text-slate-900 break-all">{value}</div>
                  </div>
                </Tag>
              );
            })}
            <div className="panel p-5 font-mono text-xs text-slate-600 tracking-wider">USDOT {b.usdot} · {b.mc}</div>
          </div>
        </div>
        <div className="container-x mt-10">
          <iframe title="Map to Echevarria Trucking" data-testid="contact-map"
            src={`https://maps.google.com/maps?q=${encodeURIComponent(b.map_query)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
            className="w-full h-[380px] border border-slate-200 grayscale-[60%] contrast-125" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </div>
      </section>
    </>
  );
}
